import type { VacationOverviewDocument } from '../../models/vacationOverview.types';
import { toIsoDay, parseIsoDay } from '../overviewDates';
import { periodPhase } from './panoramaPeriodPhase';
import { pickRequestItems, pickPendingDeadlineItems } from './panoramaRequestItems';
import { addDays } from 'date-fns';

export interface DisplayDate {
	startDate: string;
	endDate: string | null;
	days: number | null;
	kind: 'current' | 'scheduled' | 'request' | 'request-approved' | 'request-rejected' | 'taken' | 'approval-deadline' | 'deadline-passed' | 'deadline' | 'acquiring';
	tone: 'danger' | 'warning' | 'muted' | 'default';
	fraction?: number;
}

export interface DateFilter {
	month?: string | null;
	year?: string | null;
	week?: string | null;
}

export function hasDateFilter(filter?: DateFilter): boolean {
	return Boolean(filter?.month || filter?.year || filter?.week);
}

export function periodOverlapsDateFilter(startDate: string, endDate: string, filter: DateFilter): boolean {
	if (filter.week) {
		const weekStart = parseIsoDay(filter.week);
		if (!weekStart) return false;
		const weekEnd = addDays(weekStart, 6);
		const weekEndIso = toIsoDay(weekEnd);
		return startDate <= weekEndIso && endDate >= filter.week;
	}

	let year = parseInt(startDate.slice(0, 4), 10);
	let month = parseInt(startDate.slice(5, 7), 10);
	const lastYear = parseInt(endDate.slice(0, 4), 10);
	const lastMonth = parseInt(endDate.slice(5, 7), 10);

	while (year < lastYear || (year === lastYear && month <= lastMonth)) {
		const yearMatches = !filter.year || parseInt(filter.year, 10) === year;
		const monthMatches = !filter.month || parseInt(filter.month, 10) === month;
		if (yearMatches && monthMatches) return true;

		month += 1;
		if (month > 12) {
			month = 1;
			year += 1;
		}
	}

	return false;
}

function getTone(kind: DisplayDate['kind']): DisplayDate['tone'] {
	if (kind === 'deadline-passed' || kind === 'approval-deadline') return 'danger';
	if (kind === 'deadline') return 'warning';
	if (kind === 'taken' || kind === 'acquiring') return 'muted';
	return 'default';
}

export function nextAcquisitionDate(balance: any, todayIso: string): string | null {
	if (!balance) return null;
	if (balance.acquisitionEnd) return balance.acquisitionEnd;
	if (!balance.entitledFrom) return null;

	const entitled = new Date(balance.entitledFrom + 'T00:00:00');
	const today = new Date(todayIso + 'T00:00:00');

	if (entitled > today) return balance.entitledFrom;

	const nextAnniversary = new Date(entitled.getFullYear() + Math.ceil((today.getTime() - entitled.getTime()) / (365.25 * 86400000)), entitled.getMonth(), entitled.getDate());
	while (nextAnniversary <= today) {
		nextAnniversary.setFullYear(nextAnniversary.getFullYear() + 1);
	}
	return toIsoDay(nextAnniversary);
}


export function pickTodayDate(
	status: string,
	doc: VacationOverviewDocument,
	referenceDate: Date,
	daysToConcessive: number | null
): DisplayDate[] {
	const todayIso = toIsoDay(referenceDate);

	const items: DisplayDate[] = [];

	if (status === 'on-vacation') {
		const currentPeriod = doc.periods.find(p => periodPhase(p, todayIso) === 'current');
		if (currentPeriod) {
			items.push({
				startDate: currentPeriod.startDate,
				endDate: currentPeriod.endDate,
				days: currentPeriod.days,
				kind: 'current',
				tone: 'default'
			});
		}
	}

	if (status === 'scheduled') {
		const nextScheduled = doc.periods
			.filter(p => periodPhase(p, todayIso) === 'future')
			.sort((a, b) => a.startDate.localeCompare(b.startDate))[0];
		if (nextScheduled) {
			items.push({
				startDate: nextScheduled.startDate,
				endDate: nextScheduled.endDate,
				days: nextScheduled.days,
				kind: 'scheduled',
				tone: 'default'
			});
		}
	}

	if (status === 'taken') {
		const lastTaken = doc.periods
			.filter(p => periodPhase(p, todayIso) === 'past')
			.sort((a, b) => b.endDate.localeCompare(a.endDate))[0];
		if (lastTaken) {
			items.push({
				startDate: lastTaken.startDate,
				endDate: lastTaken.endDate,
				days: lastTaken.days,
				kind: 'taken',
				tone: 'muted'
			});
		}
	}

	if (status === 'expired') {
		if (doc.balance?.nextDeadline) {
			items.push({
				startDate: doc.balance.nextDeadline,
				endDate: null,
				days: null,
				kind: 'deadline-passed',
				tone: getTone('deadline-passed')
			});
		}
	}

	if (status === 'expiring-soon') {
		if (doc.balance?.nextDeadline) {
			items.push({
				startDate: doc.balance.nextDeadline,
				endDate: null,
				days: null,
				kind: 'deadline',
				tone: getTone('deadline')
			});
		}
	}

	if (status === 'none') {
		if (doc.balance?.nextDeadline) {
			items.push({
				startDate: doc.balance.nextDeadline,
				endDate: null,
				days: null,
				kind: 'deadline',
				tone: 'muted'
			});
		} else {
			const acquisDate = nextAcquisitionDate(doc.balance, todayIso);
			if (acquisDate) {
				items.push({
					startDate: acquisDate,
					endDate: null,
					days: null,
					kind: 'acquiring',
					tone: 'muted'
				});
			}
		}
	}

	if (status === 'acquiring') {
		const acquisDate = nextAcquisitionDate(doc.balance, todayIso);
		if (acquisDate) {
			items.push({
				startDate: acquisDate,
				endDate: null,
				days: null,
				kind: 'acquiring',
				tone: 'muted'
			});
		}
	}

	return items;
}

export function pickFilteredItems(
	doc: VacationOverviewDocument,
	status: string,
	daysToConcessive: number | null,
	filter: DateFilter,
	referenceDate: Date
): DisplayDate[] {
	const todayIso = toIsoDay(referenceDate);
	const items: DisplayDate[] = [];

	doc.periods
		.filter(p => periodOverlapsDateFilter(p.startDate, p.endDate, filter))
		.sort((a, b) => a.startDate.localeCompare(b.startDate))
		.forEach(p => {
			const phase = periodPhase(p, todayIso);
			let kind: DisplayDate['kind'] = 'scheduled';
			if (phase === 'past') kind = 'taken';
			else if (phase === 'current') kind = 'current';

			items.push({
				startDate: p.startDate,
				endDate: p.endDate,
				days: p.days,
				kind,
				tone: getTone(kind)
			});
		});

	if (doc.openRequest?.startDate && doc.openRequest.endDate && periodOverlapsDateFilter(doc.openRequest.startDate, doc.openRequest.endDate, filter)) {
		items.push({
			startDate: doc.openRequest.startDate,
			endDate: doc.openRequest.endDate,
			days: doc.openRequest.totalDays,
			kind: 'request',
			tone: 'default'
		});
	}

	const pendingDeadlineItems = pickPendingDeadlineItems(doc, referenceDate, daysToConcessive);
	pendingDeadlineItems.forEach(item => {
		if (periodOverlapsDateFilter(item.startDate, item.startDate, filter)) {
			items.push(item);
		}
	});

	if (status === 'expired' && doc.balance?.nextDeadline) {
		if (periodOverlapsDateFilter(doc.balance.nextDeadline, doc.balance.nextDeadline, filter)) {
			items.push({
				startDate: doc.balance.nextDeadline,
				endDate: null,
				days: null,
				kind: 'deadline-passed',
				tone: getTone('deadline-passed')
			});
		}
	}

	return items.sort((a, b) => a.startDate.localeCompare(b.startDate));
}

export function pickStatusItems(
	status: string,
	doc: VacationOverviewDocument,
	referenceDate: Date,
	daysToConcessive: number | null
): DisplayDate[] {
	const todayIso = toIsoDay(referenceDate);

	if (status === 'pending' || status === 'approval-urgent') {
		return pickRequestItems(doc, referenceDate, status as any, daysToConcessive);
	}

	if (status === 'scheduled') {
		const items: DisplayDate[] = [];
		doc.periods
			.filter(p => periodPhase(p, todayIso) === 'future')
			.sort((a, b) => a.startDate.localeCompare(b.startDate))
			.forEach(p => {
				items.push({
					startDate: p.startDate,
					endDate: p.endDate,
					days: p.days,
					kind: 'scheduled',
					tone: 'default'
				});
			});
		return items;
	}

	if (status === 'on-vacation') {
		const items: DisplayDate[] = [];
		const current = doc.periods.find(p => periodPhase(p, todayIso) === 'current');
		if (current) {
			items.push({
				startDate: current.startDate,
				endDate: current.endDate,
				days: current.days,
				kind: 'current',
				tone: 'default'
			});
		}
		doc.periods
			.filter(p => periodPhase(p, todayIso) === 'future')
			.sort((a, b) => a.startDate.localeCompare(b.startDate))
			.forEach(p => {
				items.push({
					startDate: p.startDate,
					endDate: p.endDate,
					days: p.days,
					kind: 'scheduled',
					tone: 'default'
				});
			});
		return items;
	}

	if (status === 'taken') {
		const lastTaken = doc.periods
			.filter(p => periodPhase(p, todayIso) === 'past')
			.sort((a, b) => b.endDate.localeCompare(a.endDate))[0];
		if (lastTaken) {
			return [{
				startDate: lastTaken.startDate,
				endDate: lastTaken.endDate,
				days: lastTaken.days,
				kind: 'taken',
				tone: 'muted'
			}];
		}
		return [];
	}

	return pickTodayDate(status, doc, referenceDate, daysToConcessive);
}
